import { axiosReq } from "@/lib/axios";
import { queryClient } from "@/lib/react-query" 
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useMemo } from "react";

/* 
 * This file contains code for getting images from gallery db ( normal and wishlisted images )
 * */

export type ImageBody = {
  image_url: string;
  catalogue_item: string;
  id: number;
  prompt: string;
  wishlist: boolean;
  post_id: string[];
};

export type CatalogImageBody = {
  catalogue: string;
  images: ImageBody[];
};

export const useGetGallery = (open: boolean, catalogue: string | undefined) => {
  const request = async (pageParam: number): Promise<{
    data: CatalogImageBody;
  }> => {
    const response = await axiosReq.post('/api/landing/gallery-catalogue/', {
      page: pageParam,
      catalogue: catalogue
    });
    return response.data;
  };

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["gallery-catalogue", catalogue],
    queryFn: ({ pageParam = 1 }) => request(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const hasImages = lastPage.data?.images.length > 0;
      if (!hasImages) return undefined;
      return allPages.length + 1;
    },
    enabled: open && !!catalogue, // fetch only if open is true and catalogue is provided
  });

  const gallery = useMemo(() => {
    if (!data) return [];

    // Since only one catalogue is returned, flatten all pages' images into a single array
    const allImages: ImageBody[] = data.pages.flatMap(page => page.data.images);
    console.log(allImages)

    return allImages;
  }, [data]);

  return {
    gallery,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  };
};

export const useGetVideoGallery = (open: boolean) => {
  const request = async (pageParam: number): Promise<{
    data: CatalogImageBody;
  }> => {
    const response = await axiosReq.post('/api/landing/gallery-catalogue/', {
      page: pageParam,
      catalogue: "video"
    });
    return response.data;
  };

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["video-catalogue"],
    queryFn: ({ pageParam = 1 }) => request(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const hasImages = lastPage.data?.images.length > 0;
      if (!hasImages) return undefined;
      return allPages.length + 1;
    },
    enabled: open // fetch only if open is true and catalogue is provided
  });

  const gallery = useMemo(() => {
    if (!data) return [];

    // Since only one catalogue is returned, flatten all pages' images into a single array
    const allImages: ImageBody[] = data.pages.flatMap(page => page.data.images);
    console.log(allImages)

    return allImages;
  }, [data]);

  return {
    gallery,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  };
};

export const useGetDefaultGallery = (open: boolean) => {

  const request = async (pageParam: number): Promise<{
    data: CatalogImageBody[]
  }> => {
    const response = await axiosReq.post('/api/landing/gallery/', {
      page: pageParam
    })
    return response.data
  }

  const { data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status, } = useInfiniteQuery({
      queryKey: ["gallery"],
      queryFn: ({ pageParam = 1 }) => request(pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const hasImages = lastPage.data.some(item => item.images.length > 0);
        if (!hasImages) return undefined;
        return allPages.length + 1;
      },
      enabled: open,
    });

  const gallery = useMemo(() => {
    if (!data) return [];

    const allImages: ImageBody[] = [];

    data.pages.forEach(page => {
      page.data.forEach(item => {
        allImages.push(...item.images);
      });
    });

    return allImages;
  }, [data]);

  return {
    gallery,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status
  }
}


// Custom hook to get wishlisted images
export const useGetWishlist = (open: boolean) => {

  const request = async (pageParam: number): Promise<{
    data: ImageBody[]
  }> => {
    const response = await axiosReq.post('/api/landing/wishlist/', {
      page: pageParam
    })
    return response.data
  }

  const { data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status, } = useInfiniteQuery({
      queryKey: ["wishlist"],
      queryFn: ({ pageParam = 1 }) => request(pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.length === 0) return undefined;
        return allPages.length + 1;
      },
      enabled: open,
    });

  let wishlist = data?.pages.flatMap(page => page.data)

  return {
    wishlist,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status
  }
}


// Custom hook to add wishlist image
export const useWishlistImage = () => {

  const request = async (id: number): Promise<{ message: string }> => {
    const response = await axiosReq.post('/api/landing/set-wishlist/', { id });
    return response.data
  }

  const { mutate: wishlistImage, status } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      if (data) {

        posthog.capture('image wishlisted')
        queryClient.invalidateQueries({ queryKey: ["wishlist"] })
      }
    }
  })

  return { wishlistImage, status }

}


export type Catalog = {
  image_url: string
  name: string,
  unique_identifier: string,
}

export const useGetCatalog = (open: boolean) => {

  const request = async (): Promise<{
    catalogue: Catalog[]
  }> => {
    const response = await axiosReq.get('/api/landing/catalogue/')
    return response.data
  }

  const { data: catalogData } = useQuery({
    queryKey: ["catalog"],
    queryFn: request,
    enabled: open
  })

  return { catalogData }
}

